import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { daysUntilRenewal } from 'core/util';
import { WARRANTY_BASE } from 'core/constants';

export interface Market {
  id: number;
  image: string | null;
  image_id: number | null;
  market_email: string;
  market_phone: string;
  market_tier: number;
  name: string;
  requests: unknown | null;
  timezone: string;
  type: string;
  type_id: number;
}

export type HomeCountry = 'can' | 'usa';

export interface Home {
  id: number;
  name: string;
  catalog_status: string;
  description: string | null;
  image: string | null;
  image_id: number | null;
  address1: string;
  address2: string;
  city: string;
  country: HomeCountry | null;
  postal_code: string;
  market_timezone: string;
  state: string;
  created: string;
  market: Market;
  product_count: number;
  registered_product_count: number;
  subscriptions: Subscription[];
  type: string;
  type_id: number;
  typed: number;
  is_cataloged: boolean;
  is_diy: boolean;
  avid_custsurvey_id: string | null;
  avid_warranty_id: string | null;
}

export interface Subscription {
  active: boolean;
  auto_renew_last: string | null;
  demo: boolean;
  expires: string;
  id: number;
  image: string | null;
  image_id: number | null;
  name: string;
  plan_description: string;
  type: string;
  type_id: number;
  typed: number;
}

export interface GlobalAccountSettings {
  admin: boolean;
  auto_renew: boolean | null;
  auto_renew_next: string | null;
  builder_id: number | null;
  demo: boolean;
  expired: boolean;
  expires: null | string;
  home_id: number;
  impersonating: unknown | null;
  plan: string;
  user_id: number;
  avid_warranty_url: string | null;
  recurly_status: string;
}

interface HomeState {
  homes: Home[];
  builderLogo: string;
  currentHome: Home | null;
  globalAccountSettings: GlobalAccountSettings | null;
  isExpired: boolean | null;
  isEligibleRenewal: boolean | null;
  warrantyURL: string | null;
}

export const initialState: HomeState = {
  homes: [],
  builderLogo: '',
  currentHome: null,
  globalAccountSettings: null,
  isExpired: null,
  isEligibleRenewal: null,
  warrantyURL: null
};

export const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    setHomes: (homeState, action: PayloadAction<Home[]>) => {
      homeState.homes = action.payload;
    },
    setBuilderLogo: (homeState, action: PayloadAction<string>) => {
      homeState.builderLogo = action.payload;
    },
    setCurrentHome: (
      homeState,
      action: PayloadAction<Home | null | undefined>
    ) => {
      homeState.currentHome = action.payload ?? null;
    },
    setGlobalAccountSettings: (
      homeState,
      action: PayloadAction<GlobalAccountSettings | null>
    ) => {
      homeState.globalAccountSettings = action.payload;
    },
    setIsExpired: (homeState, action: PayloadAction<boolean | null>) => {
      homeState.isExpired = action.payload;
    },
    setIsEligibleRenewal: (homeState, action: PayloadAction<string | null>) => {
      const renewalDate = action.payload;
      if (!!renewalDate && daysUntilRenewal(renewalDate) < 30) {
        homeState.isEligibleRenewal = true;
      } else {
        homeState.isEligibleRenewal = false;
      }
    },
    setWarrantyURL: (homeState, action: PayloadAction<string | null>) => {
      if (action.payload !== null) {
        homeState.warrantyURL = String(WARRANTY_BASE + action.payload);
      }
    },
    updateHomeSuccess: (homeState, action: PayloadAction<Home>) => {
      const home = action.payload;
      const currentHome = homeState.homes?.find((h) => h.id === home.id);
      if (currentHome) {
        homeState.homes?.splice(homeState.homes.indexOf(currentHome), 1, home);
        homeState.currentHome = home;
      }
    },
    updateHomeFailure: (homeState, action: PayloadAction<any>) => {},
    deleteHomeSuccess: () => initialState,
    deleteHomeError: (homeState, action: PayloadAction<any>) => {},
    resetHomes: () => initialState
  }
});

export const {
  setHomes,
  setBuilderLogo,
  setCurrentHome,
  setGlobalAccountSettings,
  setIsExpired,
  setIsEligibleRenewal,
  updateHomeSuccess,
  updateHomeFailure,
  deleteHomeSuccess,
  deleteHomeError,
  resetHomes,
  setWarrantyURL
} = homeSlice.actions;

export default homeSlice.reducer;
